import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  PencilLine,
  RefreshCw,
  Sparkles,
  Award,
  XCircle,
  ListChecks,
} from "lucide-react";
import {
  enrolInWritingCourse,
  getWritingCourseForLearner,
  submitWritingExercise,
} from "@/lib/api/writing-academy.functions";

export const Route = createFileRoute("/_authenticated/portal/writing-academy/$slug")({
  component: Player,
});

function Player() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const fetchCourse = useServerFn(getWritingCourseForLearner);
  const enrol = useServerFn(enrolInWritingCourse);
  const submit = useServerFn(submitWritingExercise);

  const { data, isLoading } = useQuery({
    queryKey: ["writing-course", slug],
    queryFn: () => fetchCourse({ data: { slug } }),
  });

  const enrolMut = useMutation({
    mutationFn: () => enrol({ data: { slug } }),
    onSuccess: () => {
      toast.success("Enrolled — let's get drafting.");
      qc.invalidateQueries({ queryKey: ["writing-course", slug] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not enrol"),
  });

  const submitMut = useMutation({
    mutationFn: (vars: { exerciseId: string; draftText: string }) =>
      submit({ data: vars }),
    onSuccess: (r: any) => {
      const s = r?.ai_score;
      const v = r?.version_no;
      toast.success(
        s != null
          ? `Version ${v} — AI scored your draft ${s}/100`
          : `Version ${v} submitted for review`,
      );
      qc.invalidateQueries({ queryKey: ["writing-course", slug] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Submission failed"),
  });

  if (isLoading || !data) {
    return (
      <div className="p-10 text-center text-muted-foreground">
        <Loader2 className="size-6 animate-spin mx-auto" /> Loading course…
      </div>
    );
  }

  const { course, enrolment, lessons, exercises, submissions } = data;
  const enrolled = !!enrolment;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <Link
        to="/writing-academy"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> All writing courses
      </Link>

      <div className="mt-4 rounded-xl border bg-card p-6">
        <div className="text-xs font-mono text-muted-foreground">{course.code}</div>
        <h1 className="text-3xl font-bold mt-1">{course.title}</h1>
        <p className="text-muted-foreground mt-2">{course.summary}</p>

        {enrolment?.certificate_id ? (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-3 py-1 text-sm">
            <Award className="size-4" /> Certified — view in My Certificates
          </div>
        ) : enrolled ? (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm">
            <CheckCircle2 className="size-4" /> Enrolled
          </div>
        ) : (
          <button
            onClick={() => enrolMut.mutate()}
            disabled={enrolMut.isPending}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {enrolMut.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PencilLine className="size-4" />
            )}
            Enrol — free
          </button>
        )}
      </div>

      <div className="mt-8 space-y-8">
        {lessons.map((lesson: any) => {
          const lessonExs = exercises.filter((e: any) => e.lesson_id === lesson.id);
          return (
            <LessonBlock
              key={lesson.id}
              lesson={lesson}
              exercises={lessonExs}
              submissions={submissions}
              enrolled={enrolled}
              onSubmit={(exerciseId, draftText) =>
                submitMut.mutate({ exerciseId, draftText })
              }
              submitting={submitMut.isPending}
            />
          );
        })}
      </div>

      {enrolled && (
        <p className="mt-10 text-sm text-muted-foreground">
          Pass every exercise (70+ on review) and a reviewer signs off your certificate
          automatically. You can revise any draft after AI or reviewer feedback — each
          submission is kept as a new version.
        </p>
      )}
    </div>
  );
}

function LessonBlock({
  lesson,
  exercises,
  submissions,
  enrolled,
  onSubmit,
  submitting,
}: {
  lesson: any;
  exercises: any[];
  submissions: any[];
  enrolled: boolean;
  onSubmit: (exerciseId: string, draftText: string) => void;
  submitting: boolean;
}) {
  return (
    <section className="rounded-xl border bg-card overflow-hidden">
      <header className="border-b bg-muted/30 px-6 py-4">
        <div className="text-xs text-muted-foreground">Lesson {lesson.order_index}</div>
        <h2 className="text-xl font-semibold">{lesson.title}</h2>
      </header>
      <div className="px-6 py-5 space-y-4">
        <article className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-line">
          {lesson.body_md}
        </article>
        {lesson.drafting_tips && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
            <div className="font-medium mb-1">Drafting tips</div>
            <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
              {lesson.drafting_tips}
            </pre>
          </div>
        )}

        {exercises.map((ex) => {
          const subs = submissions
            .filter((s: any) => s.exercise_id === ex.id)
            .sort((a: any, b: any) => b.version_no - a.version_no);
          return (
            <ExerciseCard
              key={ex.id}
              exercise={ex}
              versions={subs}
              enrolled={enrolled}
              onSubmit={(text) => onSubmit(ex.id, text)}
              submitting={submitting}
            />
          );
        })}
      </div>
    </section>
  );
}

function RubricCard({ rubric }: { rubric: any[] }) {
  if (!Array.isArray(rubric) || rubric.length === 0) return null;
  const total = rubric.reduce((sum, r) => sum + (Number(r.weight) || 0), 0);
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/20 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
        <ListChecks className="size-4" /> How your draft is scored
      </div>
      <ul className="mt-2 space-y-1.5">
        {rubric.map((r: any, i: number) => (
          <li key={i} className="text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">{r.criterion}</span>
              <span className="text-xs font-mono text-amber-900/80 dark:text-amber-200/80">
                {r.weight}%
              </span>
            </div>
            {r.descriptor && (
              <p className="text-xs text-muted-foreground">{r.descriptor}</p>
            )}
          </li>
        ))}
      </ul>
      {total !== 100 && (
        <p className="mt-2 text-[11px] text-amber-900/70 dark:text-amber-200/70">
          Weights total {total}%. Pass mark on this course: 70.
        </p>
      )}
    </div>
  );
}

function ExerciseCard({
  exercise,
  versions,
  enrolled,
  onSubmit,
  submitting,
}: {
  exercise: any;
  versions: any[];
  enrolled: boolean;
  onSubmit: (text: string) => void;
  submitting: boolean;
}) {
  const latest = versions[0];
  const latestScore = latest?.admin_score ?? latest?.ai_score;
  const passed = latest?.status === "passed";
  const isRevise = !!latest && !passed; // anything not passed = revise loop

  const [draft, setDraft] = useState(() => latest?.draft_text ?? "");
  const [showHistory, setShowHistory] = useState(false);

  const wc = useMemo(() => draft.trim().split(/\s+/).filter(Boolean).length, [draft]);

  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex items-center gap-2 text-xs text-primary font-medium uppercase tracking-wider">
        <PencilLine className="size-3.5" /> Drafting exercise
      </div>
      <h3 className="font-semibold mt-1">{exercise.prompt}</h3>
      {exercise.scenario && (
        <p className="text-sm text-muted-foreground mt-2">{exercise.scenario}</p>
      )}
      <div className="text-xs text-muted-foreground mt-2">
        Length: {exercise.min_words}–{exercise.max_words} words
      </div>

      <RubricCard rubric={exercise.rubric_public ?? []} />

      {enrolled ? (
        passed ? (
          <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50/60 p-4 text-sm dark:bg-emerald-950/20 dark:border-emerald-900/40">
            <div className="font-medium text-emerald-800 dark:text-emerald-200">
              <CheckCircle2 className="inline size-4 mr-1" />
              Passed — version {latest.version_no} scored {latestScore}/100
            </div>
            <p className="text-xs text-emerald-900/80 dark:text-emerald-200/80 mt-1">
              No further revisions needed for this exercise.
            </p>
          </div>
        ) : (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                isRevise
                  ? "Edit your previous draft using the feedback below, then submit a new version…"
                  : "Type your draft here…"
              }
              className="mt-3 w-full min-h-[200px] rounded-md border bg-card p-3 text-sm font-mono"
              disabled={submitting}
            />
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground gap-3">
              <span>
                {wc} words ·{" "}
                {wc < exercise.min_words
                  ? `${exercise.min_words - wc} more needed`
                  : wc > exercise.max_words
                    ? `${wc - exercise.max_words} over limit`
                    : "within limit"}
                {latest && (
                  <span className="ml-2">· next submission = v{latest.version_no + 1}</span>
                )}
              </span>
              <button
                onClick={() => onSubmit(draft)}
                disabled={
                  submitting ||
                  wc < exercise.min_words ||
                  wc > exercise.max_words
                }
                className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : isRevise ? (
                  <RefreshCw className="size-4" />
                ) : (
                  <Sparkles className="size-4" />
                )}
                {isRevise ? "Submit revised draft" : "Submit for AI feedback"}
              </button>
            </div>
          </>
        )
      ) : (
        <p className="mt-3 text-sm text-muted-foreground italic">
          Enrol above to submit your draft.
        </p>
      )}

      {latest && <SubmissionFeedback submission={latest} label="Latest version" />}

      {versions.length > 1 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {showHistory ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            Version history ({versions.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {versions.slice(1).map((v: any) => (
                <SubmissionFeedback
                  key={v.id}
                  submission={v}
                  label={`Version ${v.version_no}`}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubmissionFeedback({
  submission,
  label,
  compact,
}: {
  submission: any;
  label: string;
  compact?: boolean;
}) {
  const s = submission;
  const feedback = s.ai_feedback || {};
  const score = s.admin_score ?? s.ai_score;
  const passed = s.status === "passed";
  const revise = s.status === "revise";
  return (
    <div
      className={`mt-3 rounded-lg border p-4 ${
        compact ? "bg-muted/20" : "bg-muted/30"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          {passed ? (
            <CheckCircle2 className="size-4 text-emerald-600" />
          ) : revise ? (
            <XCircle className="size-4 text-amber-600" />
          ) : (
            <Sparkles className="size-4 text-primary" />
          )}
          {label} · v{s.version_no}
          {score != null && (
            <span className="ml-2 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs">
              {score}/100
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(s.submitted_at).toLocaleString()}
        </span>
      </div>
      {feedback?.summary && <p className="mt-2 text-sm">{feedback.summary}</p>}
      {Array.isArray(feedback?.strengths) && feedback.strengths.length > 0 && (
        <div className="mt-2 text-sm">
          <div className="font-medium text-emerald-700 dark:text-emerald-300">
            Strengths
          </div>
          <ul className="list-disc list-inside text-muted-foreground">
            {feedback.strengths.map((x: string, i: number) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(feedback?.improvements) && feedback.improvements.length > 0 && (
        <div className="mt-2 text-sm">
          <div className="font-medium text-amber-700 dark:text-amber-300">
            Improve in your next version
          </div>
          <ul className="list-disc list-inside text-muted-foreground">
            {feedback.improvements.map((x: string, i: number) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(feedback?.rubric) && feedback.rubric.length > 0 && (
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Rubric breakdown
          </summary>
          <ul className="mt-1 space-y-1">
            {feedback.rubric.map((r: any, i: number) => (
              <li key={i} className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-1">
                <span>{r.criterion}</span>
                <span className="font-mono">
                  {r.awarded}/{r.weight}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {s.admin_feedback && (
        <div className="mt-3 rounded-md border bg-card p-3 text-sm">
          <div className="font-medium">Reviewer note</div>
          <p className="text-muted-foreground mt-1 whitespace-pre-line">
            {s.admin_feedback}
          </p>
        </div>
      )}
    </div>
  );
}
